import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './SideBar.js';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { Pie, Bar } from 'react-chartjs-2';
import SalesChart from './SalesChart.js';
import { Chart, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineController, LineElement, Title, Filler } from 'chart.js';
import PurchaseChart from './PurchaseChart.js';
import PaymentChart from './PaymentChart.js';
import ReceiptChart from './RecepitChart.js';

// MUI Imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';

Chart.register(LineController, LineElement, Title, Filler, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const DashboardPage = () => {
  const location = useLocation();
  const [companyName, setCompanyName] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [totalReceivables, setTotalReceivables] = useState(0);
  const [totalPayables, setTotalPayables] = useState(0);
  const [currentTotalCash, setCurrentTotalCash] = useState(0);
  const [currentTotalCredit, setCurrentTotalCredit] = useState(0);
  const [currentCard, setCurrentCard] = useState(0);
  const [currentWallet, setCurrentWallet] = useState(0);
  const [currentCreditNote, setCurrentCreditNote] = useState(0);
  const [currentLocalAmount, setCurrentLocalAmount] = useState(0);
  const [currentOutsideAmount, setCurrentOutsideAmount] = useState(0);
  const [purchaseData, setPurchaseData] = useState([]);
  const [currentWholesaleAmount, setCurrentWholesaleAmount] = useState(0);
  const [currentTaxFreeAmount, setCurrentTaxFreeAmount] = useState(0);
  const [currentJobworkAmount, setCurrentJobworkAmount] = useState(0);
  const [currentBranchTransferAmount, setCurrentBranchTransferAmount] = useState(0);
  const [currentTotalSales, setCurrentTotalSales] = useState(0);
  const [currentTotalPurchase, setCurrentTotalPurchase] = useState(0);
  const [selectedSaleType, setSelectedSaleType] = useState('All');
  const [salesInterval, setSalesInterval] = useState('daily');
  const [purchaseInterval, setPurchaseInterval] = useState('daily');
  const [paymentInterval, setPaymentInterval] = useState('daily');
  const [receiptInterval, setReceiptInterval] = useState('daily');


  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlCompanyName = queryParams.get('companyName');
    const urlFromDate = queryParams.get('fromDate');
    const urlToDate = queryParams.get('toDate');

    if (urlCompanyName && urlFromDate && urlToDate) {
      localStorage.setItem('companyName', urlCompanyName);
      localStorage.setItem('fromDate', urlFromDate);
      localStorage.setItem('toDate', urlToDate);
      setCompanyName(urlCompanyName);
      setFromDate(urlFromDate);
      setToDate(urlToDate);
    } else {
      setCompanyName(localStorage.getItem('companyName') || '');
      setFromDate(localStorage.getItem('fromDate') || '');
      setToDate(localStorage.getItem('toDate') || '');
    }
  }, [location.search]);

  const fetchOutstandingData = useCallback(async (endpoint, setTotal) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
      const compCode = selectedCompany ? selectedCompany.CompCode : '';

      const response = await axios.get(`${API_BASE_URL}/api/${endpoint}`, {
        params: { date: today, CompCode: compCode, dueDays: 0 },
        headers: {
          'UserId': localStorage.getItem('userId'),
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (Array.isArray(response.data)) {
        const total = response.data.reduce((acc, item) => acc + (parseFloat(item.field11) || 0), 0);
        setTotal(total.toFixed(2));
      } else {
        console.error('Data is not an array:', response.data);
        setTotal(0);
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
    }
  }, []);

  const fetchPaymentData = useCallback(async (sptype = '') => {
    try {
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
      const compCode = selectedCompany ? selectedCompany.CompCode : '';

      const finalSptype = sptype === 'All' ? '' : sptype;

      const response = await axios.get(`${API_BASE_URL}/api/get-summary`, {
        params: { CompCode: compCode, sptype: finalSptype },
        headers: {
          'UserId': localStorage.getItem('userId'),
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        const getAmount = (type) =>
          response.data
            .filter((item) => item.PAYMENT === type)
            .reduce((acc, item) => acc + (parseFloat(item.Amount) || 0), 0);

        const cashTotal = getAmount('CASH');
        const creditTotal = getAmount('CREDIT');
        const cardTotal = getAmount('CARD');
        const walletTotal = getAmount('WALLET');
        const creditNoteTotal = getAmount('CREDIT NOTE');
        const totalSales = cashTotal + creditTotal + cardTotal + walletTotal + creditNoteTotal;

        setCurrentTotalSales(totalSales);
        setCurrentTotalCash(cashTotal);
        setCurrentTotalCredit(creditTotal);
        setCurrentCard(cardTotal);
        setCurrentWallet(walletTotal);
        setCurrentCreditNote(creditNoteTotal);
      } else {
        setCurrentTotalSales(0);
        setCurrentTotalCash(0);
        setCurrentTotalCredit(0);
        setCurrentCard(0);
        setCurrentWallet(0);
        setCurrentCreditNote(0);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
    }
  }, []);

  const fetchPurchaseSummary = useCallback(async () => {
    try {
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));
      const compCode = selectedCompany ? selectedCompany.CompCode : '';

      const response = await axios.get(`${API_BASE_URL}/api/purchase-summary`, {
        params: { CompCode: compCode },
        headers: {
          'UserId': localStorage.getItem('userId'),
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        const localAmount = response.data.find(item => item.sptype === 'LOCAL')?.Amount || 0;
        const outsideAmount = response.data.find(item => item.sptype === 'OUTSIDE')?.Amount || 0;
        const wholesaleAmount = response.data.find(item => item.sptype === 'WHOLESALE')?.Amount || 0;
        const taxFreeAmount = response.data.find(item => item.sptype === 'TAX FREE')?.Amount || 0;
        const jobworkAmount = response.data.find(item => item.sptype === 'JOBWORK')?.Amount || 0;
        const branchTransferAmount = response.data.find(item => item.sptype === 'BRANCHTRANSFER')?.Amount || 0;

        const totalPurchase = localAmount + outsideAmount + wholesaleAmount + taxFreeAmount + jobworkAmount + branchTransferAmount;
        setCurrentTotalPurchase(totalPurchase);

        setCurrentLocalAmount(localAmount);
        setCurrentOutsideAmount(outsideAmount);
        setCurrentWholesaleAmount(wholesaleAmount);
        setCurrentTaxFreeAmount(taxFreeAmount);
        setCurrentJobworkAmount(jobworkAmount);
        setCurrentBranchTransferAmount(branchTransferAmount);
      } else {
        console.warn('No purchase data received or data is not in expected format');
        setPurchaseData([]);
        setCurrentTotalPurchase(0);
      }
    } catch (error) {
      console.error('Error fetching purchase summary:', error);
    }
  }, []);


  useEffect(() => {
    if (companyName && fromDate && toDate) {
      fetchOutstandingData('outstanding-receivables', setTotalReceivables);
      fetchOutstandingData('outstanding-payables', setTotalPayables);
      fetchPaymentData();
      fetchPurchaseSummary();
    }
  }, [companyName, fromDate, toDate, fetchPaymentData, fetchOutstandingData, fetchPurchaseSummary]);

  useEffect(() => {
    if (Array.isArray(purchaseData) && purchaseData.length > 0) {
      const localAmount = purchaseData.find(item => item.sptype === 'LOCAL')?.Amount || 0;
      const outsideAmount = purchaseData.find(item => item.sptype === 'OUTSIDE')?.Amount || 0;
      const wholesaleAmount = purchaseData.find(item => item.sptype === 'WHOLESALE')?.Amount || 0;
      const taxFreeAmount = purchaseData.find(item => item.sptype === 'TAX FREE')?.Amount || 0;
      const jobworkAmount = purchaseData.find(item => item.sptype === 'JOBWORK')?.Amount || 0;
      const branchTransferAmount = purchaseData.find(item => item.sptype === 'BRANCHTRANSFER')?.Amount || 0;

      const totalPurchase = localAmount + outsideAmount + wholesaleAmount + taxFreeAmount + jobworkAmount + branchTransferAmount;
      setCurrentTotalPurchase(totalPurchase);

      setCurrentLocalAmount(localAmount);
      setCurrentOutsideAmount(outsideAmount);
      setCurrentWholesaleAmount(wholesaleAmount);
      setCurrentTaxFreeAmount(taxFreeAmount);
      setCurrentJobworkAmount(jobworkAmount);
      setCurrentBranchTransferAmount(branchTransferAmount);
    }
  }, [purchaseData]);

  const purchaseTypes = [
    { type: 'LOCAL', amount: currentLocalAmount, color: '#4BC0C0' },
    { type: 'OUTSIDE', amount: currentOutsideAmount, color: '#FF9F40' },
    { type: 'WHOLESALE', amount: currentWholesaleAmount, color: '#FF6384' },
    { type: 'TAX FREE', amount: currentTaxFreeAmount, color: '#36A2EB' },
    { type: 'JOBWORK', amount: currentJobworkAmount, color: '#FFCE56' },
    { type: 'BRANCHTRANSFER', amount: currentBranchTransferAmount, color: '#9966FF' }
  ];

  const filteredPurchaseTypes = purchaseTypes.filter(item => item.amount > 0);

  const labelsForPurchase = filteredPurchaseTypes.map(item => item.type);
  const dataForPurchase = filteredPurchaseTypes.map(item => item.amount);
  const backgroundColorsForPurchase = filteredPurchaseTypes.map(item => item.color);

  const pieData = {
    labels: labelsForPurchase,
    datasets: [{
      data: dataForPurchase,
      backgroundColor: backgroundColorsForPurchase,
      hoverBackgroundColor: backgroundColorsForPurchase
    }]
  };

  const salesTypes = [
    { type: 'CASH', amount: currentTotalCash, color: '#FF6384' },
    { type: 'CREDIT', amount: currentTotalCredit, color: '#36A2EB' },
    { type: 'CARD', amount: currentCard, color: '#FFCE56' },
    { type: 'WALLET', amount: currentWallet, color: '#4BC0C0' },
    { type: 'CR-NOTE', amount: currentCreditNote, color: '#9966FF' }
  ];

  const filteredSalesTypes = salesTypes.filter(item => item.amount > 0);

  const labelsForSales = filteredSalesTypes.map(item => item.type);
  const dataForSales = filteredSalesTypes.map(item => item.amount);
  const backgroundColorsForSales = filteredSalesTypes.map(item => item.color);

  const pieDataForSales = {
    labels: labelsForSales,
    datasets: [{
      data: dataForSales,
      backgroundColor: backgroundColorsForSales,
      hoverBackgroundColor: backgroundColorsForSales
    }]
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 10,
            family: 'Arial',
          },
          padding: 15,
          boxWidth: 15,
          boxHeight: 15,
        },
      },
    },
  };

  const barData = {
    labels: ['Total Receivables', 'Total Payables'],
    datasets: [{
      data: [totalReceivables, totalPayables],
      backgroundColor: [
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 99, 132, 0.6)'
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 99, 132, 1)'
      ],
      borderWidth: 2,
      borderRadius: 5
    }]
  };

  const options = {
    scales: {
      y: {
        ticks: {
          stepSize: 50000,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          generateLabels: (chart) => {
            return [
              {
                text: 'Receivables',
                fillStyle: 'rgba(54, 162, 235, 0.6)',
              },
              {
                text: 'Payables',
                fillStyle: 'rgba(255, 99, 132, 0.6)',
              },
            ];
          },
          padding: 20,
          boxWidth: 14,
          boxHeight: 14,
        },
        onClick: () => { },
        onHover: () => { },
      },
    },
  };

  const handleSaleTypeChange = (event) => {
    const selected = event.target.value;
    setSelectedSaleType(selected);
    fetchPaymentData(selected);
  };

  useEffect(() => {
    if (companyName && fromDate && toDate) {
      fetchPaymentData('All');
    }
  }, [companyName, fromDate, toDate, fetchPaymentData]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);

  const handleSidebarToggle = useCallback((collapsed) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  return (
    <div className="flex h-screen md:h-[100dvh] overflow-hidden bg-slate-50 font-sans">
      <Sidebar onToggle={handleSidebarToggle} />

      <Box
        component="main"
        className={`flex-1 flex flex-col h-full transition-all duration-300 ${isSidebarCollapsed ? 'ml-0' : 'ml-0 md:ml-[250px]'}`}
      >
        {/* Modern Header */}
        <div className="bg-white sticky top-0 z-30 pl-16 pr-6 md:px-6 py-4 border-b border-slate-200 flex justify-between items-center shadow-sm">
          <div>
            <Typography variant="h5" className="font-bold text-slate-800 tracking-tight">
              {companyName || 'Dashboard'}
            </Typography>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <Typography variant="body2" className="text-slate-500 font-medium">
                Period: {fromDate} - {toDate}
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Future: Add User Profile or Notifications here */}
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 scroll-smooth bg-slate-50/50">
          <div className="w-full mx-auto space-y-6">

            {/* Top Row: Key Metrics & Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {/* Sales Card */}
              <div className="flex flex-col">
                <Card className="w-full h-full flex-1 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white group relative flex flex-col">
                  <CardContent className="flex flex-col flex-1 p-5">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <Typography variant="h6" className="font-bold text-slate-700">Today's Sales</Typography>
                        <Typography variant="body2" className="text-slate-400 font-medium">Sales distribution by type</Typography>
                      </div>
                      <FormControl size="small" variant="outlined" className="min-w-[100px]">
                        <Select
                          value={selectedSaleType}
                          onChange={handleSaleTypeChange}
                          className="bg-slate-50 text-xs h-8 shadow-sm border-slate-200"
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          }}
                        >
                          <MenuItem value="All">All Types</MenuItem>
                          <MenuItem value="LOCAL">Local</MenuItem>
                          <MenuItem value="OUTSIDE">Outside</MenuItem>
                          <MenuItem value="EXPORT">Export</MenuItem>
                          <MenuItem value="WHOLESALE">Wholesale</MenuItem>
                          <MenuItem value="BRANCHTRANSFER">Branch Transfer</MenuItem>
                          <MenuItem value="JOBWORK">Jobwork</MenuItem>
                          <MenuItem value="TAX FREE">Tax Free</MenuItem>
                        </Select>
                      </FormControl>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center">
                      {currentTotalSales > 0 ? (
                        <>
                          <Box className="w-full max-w-[160px] relative mb-2">
                            <Pie data={pieDataForSales} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: false } } }} />
                          </Box>
                          <div className="flex-1 flex flex-col justify-center w-full">
                            {currentTotalSales > 0 ? (
                              <div className="w-full max-w-sm mx-auto divide-y divide-slate-100">
                                {filteredSalesTypes.map((item) => (
                                  <div
                                    key={item.type}
                                    className="flex items-center justify-center py-0.5"
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className="w-1.5 h-1.5 rounded-full"
                                        style={{ backgroundColor: item.color }}
                                      />
                                      <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                                        {item.type}
                                      </span>
                                      <span className="text-[11px] text-slate-700 font-semibold">
                                        ₹{item.amount.toLocaleString("en-IN")}
                                      </span>
                                    </div>
                                  </div>


                                ))}
                              </div>
                            ) : (
                              <div className="text-center text-slate-400 py-8">
                                <Typography variant="body2">No sales data available</Typography>
                              </div>
                            )}
                          </div>

                        </>
                      ) : (
                        <div className="text-center text-slate-400 py-8">
                          <Typography variant="body2">No sales data available</Typography>
                        </div>
                      )}
                    </div>

                    <div className="text-center mt-4 pt-4 border-t border-slate-100">
                      <Typography variant="caption" className="text-slate-400 uppercase tracking-wider font-bold text-[10px]">Total Sales</Typography>
                      <Typography variant="h4" className="font-bold text-slate-800 mt-1 tracking-tight">
                        ₹{currentTotalSales.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Purchases Card */}
              <div className="flex flex-col">
                <Card className="w-full h-full flex-1 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white flex flex-col">
                  <CardContent className="flex flex-col flex-1 p-5">
                    <div className="mb-4">
                      <Typography variant="h6" className="font-bold text-slate-700">Today's Purchases</Typography>
                      <Typography variant="body2" className="text-slate-400 font-medium">Purchase distribution by type</Typography>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center">
                      {currentTotalPurchase > 0 ? (
                        <>
                          <Box className="w-full max-w-[160px] relative mb-4">
                            <Pie data={pieData} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: false } } }} />
                          </Box>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full mt-2">
                            {filteredPurchaseTypes.map((item) => (
                              <div key={item.type} className="flex items-center gap-3 text-xs border-b border-slate-50 pb-1">
                                <div className="flex items-center gap-1.5 min-w-[80px]">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                                  <span className="text-slate-600 font-medium">{item.type}</span>
                                </div>
                                <span className="text-slate-800 font-bold">₹{item.amount.toLocaleString('en-IN')}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center text-slate-400 py-8 flex flex-col items-center">
                          <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-2">
                            <span className="text-2xl">🛒</span>
                          </div>
                          <Typography variant="body2">No purchase data available</Typography>
                        </div>
                      )}
                    </div>

                    <div className="text-center mt-4 pt-4 border-t border-slate-100">
                      <Typography variant="caption" className="text-slate-400 uppercase tracking-wider font-bold text-[10px]">Total Purchases</Typography>
                      <Typography variant="h4" className="font-bold text-slate-800 mt-1 tracking-tight">
                        ₹{currentTotalPurchase.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Outstanding Card */}
              <div className="flex flex-col">
                <Card className="w-full h-full flex-1 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white flex flex-col">
                  <CardContent className="flex flex-col flex-1 p-5">
                    <div className="mb-4">
                      <Typography variant="h6" className="font-bold text-slate-700">Outstanding</Typography>
                      <Typography variant="body2" className="text-slate-400 font-medium">Receivables vs Payables</Typography>
                    </div>

                    <Box className="w-full flex-1 flex items-center justify-center">
                      <Bar data={barData} options={options} />
                    </Box>

                    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                      <div className="text-center p-2 bg-blue-50 rounded-xl">
                        <Typography variant="caption" className="text-blue-600 font-bold block mb-1">RECEIVABLES</Typography>
                        <Typography variant="h6" className="font-bold text-slate-800">₹{Math.floor(totalReceivables).toLocaleString('en-IN')}</Typography>
                      </div>
                      <div className="text-center p-2 bg-red-50 rounded-xl">
                        <Typography variant="caption" className="text-red-500 font-bold block mb-1">PAYABLES</Typography>
                        <Typography variant="h6" className="font-bold text-slate-800">₹{Math.floor(totalPayables).toLocaleString('en-IN')}</Typography>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Middle Row: Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <Card className="shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <Typography variant="h6" className="font-bold text-slate-700">Sales Trends</Typography>
                      <FormControl size="small" variant="outlined" className="min-w-[100px]">
                        <Select
                          value={salesInterval}
                          onChange={(e) => setSalesInterval(e.target.value)}
                          className="bg-slate-50 text-xs h-8 shadow-sm border-slate-200"
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <SalesChart interval={salesInterval} />
                  </CardContent>
                </Card>
              </div>
              <div>
                <Card className="shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <Typography variant="h6" className="font-bold text-slate-700">Purchase Trends</Typography>
                      <FormControl size="small" variant="outlined" className="min-w-[100px]">
                        <Select
                          value={purchaseInterval}
                          onChange={(e) => setPurchaseInterval(e.target.value)}
                          className="bg-slate-50 text-xs h-8 shadow-sm border-slate-200"
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <PurchaseChart interval={purchaseInterval} />
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Bottom Row: Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <Card className="shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <Typography variant="h6" className="font-bold text-slate-700">Payment Analysis</Typography>
                      <FormControl size="small" variant="outlined" className="min-w-[100px]">
                        <Select
                          value={paymentInterval}
                          onChange={(e) => setPaymentInterval(e.target.value)}
                          className="bg-slate-50 text-xs h-8 shadow-sm border-slate-200"
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <PaymentChart interval={paymentInterval} />
                  </CardContent>
                </Card>
              </div>
              <div>
                <Card className="shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <Typography variant="h6" className="font-bold text-slate-700">Receipt Analysis</Typography>
                      <FormControl size="small" variant="outlined" className="min-w-[100px]">
                        <Select
                          value={receiptInterval}
                          onChange={(e) => setReceiptInterval(e.target.value)}
                          className="bg-slate-50 text-xs h-8 shadow-sm border-slate-200"
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <ReceiptChart interval={receiptInterval} />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Typography variant="body2" className="text-slate-400">
                &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
              </Typography>
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
}

export default DashboardPage;
